import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import { FarmRiskType, FarmScheduleCoverageType, paymentMethod, StructureCategory } from '@ourbranch/lookups';
import { getIn } from 'formik';
import { isRentersRiskType } from '@ourbranch/farm-utils';

import { getFarmInterestedPartyItemSchema } from 'common/components/farm/additional-party/add-farm-interested-party.validation';

import {
  validHomePurchaseDate,
  validUSState,
  requiredForRisks,
  requiredIfFO6BarnsOutbuildings,
  requiredIfFO6SilosGrainBins,
  requiredIfFO6SilosGrainBinsNonTower,
  requiredIfFO6ConfinementBuildings,
  requiredIfFO6HoopBuildings,
  requiredIfFO6DwellingContents,
  requiredIfFO6Fencing,
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled,
  requiredIfFO6DwellingContentsAndHeatingEnabled,
  requiredForTowers,
  requiredForGrainStorage,
  requiredForNonTowerSilos,
  requiredIfFO6SilosGrainBinsAndHeatingEnabled,
  requiredIfFO6PoultryAndHeatingEnabled,
  validYearBuilt,
  requiredWhenCanEditCoverageAmount,
  coverageInRangeRequiredWhenEditable,
  mobileHomeSqFtValidation,
  isDwellingOrRentersRisk,
  nullifyIfNaN,
  requiredForFO6Structures,
  rc80PercentRequirement,
  invalidMobileHomeConfigForFo3,
  requiresSwimmingPoolType
} from 'common/helpers/yup-helpers';
import { brandConstants, appConfig } from 'brand-config';
import { getConstructionTypeExemptPropertyTypes } from 'common/helpers/farm-helpers';

/**
 * Helper function to get building class 1 maximum age from brand configuration
 * @param {string} state - State code (e.g., 'IL', 'MO')
 * @returns {number} Maximum age in years for Building Class 1 (defaults to 25)
 */
const getBuildingClass1MaxAge = (state = 'IL') => {
  return appConfig?.farm?.[state]?.risks[FarmRiskType.Fo6Structures]?.businessRules?.buildingClass1MaxAgeYears ?? 25;
};

/**
 * Helper function to detect if a risk is new (not yet saved)
 * @param {object} formRiskData - The risk data from form context
 * @returns {boolean} True if the risk is new (has no id), false otherwise
 */
const isNewRisk = (formRiskData) => !formRiskData?.id;

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'requiredForRisks', requiredForRisks);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6BarnsOutbuildingsAndHeatingEnabled',
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled
);
Yup.addMethod(Yup.mixed, 'requiredForTowers', requiredForTowers);
Yup.addMethod(Yup.mixed, 'requiredForGrainStorage', requiredForGrainStorage);
Yup.addMethod(Yup.mixed, 'requiredForNonTowerSilos', requiredForNonTowerSilos);
Yup.addMethod(Yup.mixed, 'requiredIfFO6SilosGrainBinsAndHeatingEnabled', requiredIfFO6SilosGrainBinsAndHeatingEnabled);
Yup.addMethod(Yup.mixed, 'requiredIfFO6PoultryAndHeatingEnabled', requiredIfFO6PoultryAndHeatingEnabled);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6DwellingContentsAndHeatingEnabled',
  requiredIfFO6DwellingContentsAndHeatingEnabled
);
Yup.addMethod(Yup.string, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBinsNonTower', requiredIfFO6SilosGrainBinsNonTower);
Yup.addMethod(Yup.string, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.number, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6DwellingContents', requiredIfFO6DwellingContents);
Yup.addMethod(Yup.number, 'requiredIfFO6Fencing', requiredIfFO6Fencing);
Yup.addMethod(Yup.number, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.mixed, 'requiredWhenCanEditCoverageAmount', requiredWhenCanEditCoverageAmount);
Yup.addMethod(Yup.mixed, 'coverageInRangeRequiredWhenEditable', coverageInRangeRequiredWhenEditable);
Yup.addMethod(Yup.string, 'validYearBuilt', validYearBuilt);
Yup.addMethod(Yup.number, 'mobileHomeSqFtValidation', mobileHomeSqFtValidation);
Yup.addMethod(Yup.number, 'nullifyIfNaN', nullifyIfNaN);
Yup.addMethod(Yup.string, 'requiredForFO6Structures', requiredForFO6Structures);
Yup.addMethod(Yup.number, 'rc80PercentRequirement', rc80PercentRequirement);
Yup.addMethod(Yup.string, 'invalidMobileHomeConfigForFo3', invalidMobileHomeConfigForFo3);

/**
 * Barn Building Class Coverage Validation
 *
 * Validates coverage fields for FO6 Barns/Outbuildings based on building class:
 * - Class 1 & 2: Must use RC loss settlement, Special Form coverage, and Snow Weight coverage
 * - Class 3: Must use ACV loss settlement (Special Form and Snow Weight must be off)
 *
 * Matches BE enforcement in farm-post-data-rules.ts:698-730
 * Related issues: #29098, #29153
 */
export const barnClassCoverageValidation = function (message) {
  return this.test('barn-class-coverage-validation', message, function (value) {
    // Get risk type and building data from correct context
    // this.from[1].value should be the risk object when validating coverage fields
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Barns/Outbuildings
    if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
      return true;
    }

    const buildingClass = getIn(formRiskData, 'building.buildingClass');
    const field = this.path.split('.').pop();

    // Class 1 & 2: Must use RC + Special Form + Snow Weight
    if (buildingClass === 1 || buildingClass === 2) {
      if (field === 'structureLossSettlement' && value !== 'RC') {
        return this.createError({
          message: `Building Class ${buildingClass} barns must use Replacement Cost loss settlement`
        });
      }

      if (field === 'specialFormCoverage' && value !== true) {
        return this.createError({
          message: `Building Class ${buildingClass} barns must include Special Form coverage`
        });
      }

      if (field === 'hasSnowWeightCoverage' && value !== true) {
        return this.createError({
          message: `Building Class ${buildingClass} barns must include Weight of Ice, Snow, or Sleet coverage`
        });
      }

      return true;
    }

    // Class 3: Must use ACV, no Special Form, no Snow Weight
    if (buildingClass === 3) {
      if (field === 'structureLossSettlement' && value !== 'ACV') {
        return this.createError({
          message: 'Building Class 3 barns must use Actual Cash Value loss settlement'
        });
      }

      if (field === 'specialFormCoverage' && value === true) {
        return this.createError({
          message: 'Building Class 3 barns cannot have Special Form coverage'
        });
      }

      if (field === 'hasSnowWeightCoverage' && value === true) {
        return this.createError({
          message: 'Building Class 3 barns cannot have Weight of Ice, Snow, or Sleet coverage'
        });
      }

      return true;
    }

    return true;
  });
};

// Register the method (backwards-compatible alias)
Yup.addMethod(Yup.mixed, 'barnType1Validation', barnClassCoverageValidation);

/**
 * Coverage C Validation
 *
 * Validates Coverage C based on residence usage and brandConfig rules.
 * - FO1: Residence-usage-based rules (Issue #28614)
 * - FO2: Loss-settlement + residence-usage rules from brandConfig (Issue #29011)
 * - FO3: Residence-usage rules from brandConfig (Issue #29011)
 * - FO4: Minimum $10,000, no maximum (Issue #29011)
 */
export const validateDwellingCoverageC = function (message) {
  return this.test('fo1-coverage-c-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const coverageA = getIn(formRiskData, 'coverages.coverageA');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    // FO1 validation
    if (riskType === FarmRiskType.Fo1) {
      const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageC;
      if (!businessRules?.byResidenceUsage) {
        return true; // No business rules defined
      }

      const rules = businessRules.byResidenceUsage[residenceUsage];
      if (!rules) {
        return true; // No rules for this residence usage
      }

      const currentValue = Number(value) || 0;
      const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

      if (currentValue < rules.min) {
        return this.createError({
          message: `Coverage C cannot be less than $${rules.min.toLocaleString()}`
        });
      }

      if (currentValue > maxValue) {
        if (residenceUsage === 'R') {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()} for tenant occupied rental properties`
          });
        }
        return this.createError({
          message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
        });
      }

      return true;
    }

    // FO2 and FO3: brandConfig-driven Coverage C validation (Issue #29011)
    if ([FarmRiskType.Fo2, FarmRiskType.Fo3].includes(riskType)) {
      const lossSettlement = getIn(formRiskData, 'coverages.lossSettlement');
      const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageC;

      // Resolve rules: FO2 uses byLossSettlement (ACV vs RC differ), FO3 uses byResidenceUsage
      let rules;
      if (businessRules?.byLossSettlement && lossSettlement) {
        rules = businessRules.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
      }
      // Fall through to byResidenceUsage as secondary lookup regardless of byLossSettlement presence
      if (!rules && businessRules?.byResidenceUsage && residenceUsage) {
        rules = businessRules.byResidenceUsage[residenceUsage];
      }

      if (rules) {
        const minValue = rules.minPctOfCoverageA ? coverageA * rules.minPctOfCoverageA : (rules.min ?? 0);
        const maxValue = rules.max === 'coverageA' ? coverageA : rules.max;

        if (value < minValue) {
          const minLabel = rules.minPctOfCoverageA
            ? `$${minValue.toLocaleString()} (${rules.minPctOfCoverageA * 100}% of Coverage A)`
            : `$${minValue.toLocaleString()}`;
          return this.createError({
            message: `Coverage C must be at least ${minLabel}`
          });
        }

        if (value > maxValue) {
          return this.createError({
            message: `Coverage C cannot exceed $${maxValue.toLocaleString()}`
          });
        }

        return true;
      }

      return true;
    }

    // FO4: Minimum $10,000, no maximum (Issue #29011)
    if (riskType === FarmRiskType.Fo4) {
      if (value != null && value < 10000) {
        return this.createError({
          message: 'Coverage C must be at least $10,000'
        });
      }

      return true;
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateDwellingCoverageC', validateDwellingCoverageC);

/**
 * FO1 Coverage D Validation
 *
 * Validates Coverage D based on residence usage for FO1 risks.
 * - Primary Residence: 20% of Coverage A (locked, not editable)
 * - Seasonal: 20% of Coverage A (locked, not editable)
 * - Tenant Occupied Rental: 10% of Coverage A (locked, not editable) (#32082)
 * - Vacant: $0 only (not editable)
 *
 * Related issues: #28614, #29855, #32082
 */
export const validateFO1CoverageD = function (message) {
  return this.test('fo1-coverage-d-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const residenceUsage = getIn(formRiskData, 'building.residenceUsage');
    const state = this.options?.context?.locationAddress?.state ?? this.options?.context?.state;

    // Only validate for FO1
    if (riskType !== FarmRiskType.Fo1) {
      return true;
    }

    // Get business rules from brand config
    const businessRules = appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.coverageD;
    if (!businessRules?.byResidenceUsage) {
      return true; // No business rules defined
    }

    const rules = businessRules.byResidenceUsage[residenceUsage];
    if (!rules) {
      return true; // No rules for this residence usage
    }

    // Skip min/max validation when coverage D is auto-calculated as a percentage of Coverage A
    if (rules.percentOfCoverageA) {
      return true;
    }

    const currentValue = Number(value) || 0;

    if (currentValue < rules.min) {
      return this.createError({
        message: `Coverage D cannot be less than $${rules.min.toLocaleString()}`
      });
    }

    if (currentValue > rules.max) {
      if (residenceUsage === 'V') {
        return this.createError({
          message: 'Coverage D is not available for vacant properties'
        });
      }
      return this.createError({
        message: `Coverage D cannot exceed $${rules.max.toLocaleString()}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateFO1CoverageD', validateFO1CoverageD);

/**
 * Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible.
 * Wind/Hail deductible is always stored as a decimal percentage (0.01 = 1%, 0.015 = 1.5%, 0.02 = 2%)
 * and must be converted to a dollar amount using coverageA before comparison.
 *
 * Related issue: #28860
 */
export const validateWindHailDeductible = function (message) {
  return this.test('wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO1, FO2, FO3, FO4 (dwelling risks)
    if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Get Coverage A to calculate dollar amounts for percentage deductibles
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    // Get the reference coverage to calculate dollar amounts for percentage deductibles
    // Percentage deductibles are represented as values < 1 (e.g., 0.01 = 1%)
    // FO-4 (renters) has no Coverage A — use Coverage C instead
    const referenceCoverage = isRentersRiskType(riskType)
      ? getIn(formRiskData, 'coverages.coverageC')
      : getIn(formRiskData, 'coverages.coverageA');

    // Calculate actual dollar amounts
    // If value < 1, it's a percentage deductible, multiply by the reference coverage
    const windHailDollarAmount =
      windHailValue < 1 ? (referenceCoverage ? windHailValue * referenceCoverage : null) : windHailValue;

    const allPerilDollarAmount =
      allPerilValue < 1 ? (referenceCoverage ? allPerilValue * referenceCoverage : null) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing
    if (windHailValue < 1 && windHailDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based Wind/Hail deductible'
      });
    }

    // If all-peril is a percentage but coverage is missing
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      // For renters risks (FO4), skip validation when coverage is missing
      // For dwelling risks, reject to enforce coverage requirement
      if (isRentersRiskType(riskType)) {
        return true;
      }
      return this.createError({
        message: 'Coverage amount is required to calculate percentage-based All-Peril deductible'
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = Math.round(windHailDollarAmount);
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${+(windHailValue * 100).toFixed(2)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${+(allPerilValue * 100).toFixed(2)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay})`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateWindHailDeductible', validateWindHailDeductible);

/**
 * FO6 Wind/Hail Deductible Validation
 *
 * Validates that Wind/Hail deductible is at least as high as the All-Peril deductible for FO6 risks.
 * Handles different coverage types:
 * - FO6Structures: Uses coverageE
 * - FO6Scheduled: Uses coverageF
 * - FO6Unscheduled: Uses coverageG (read-only, but validate for completeness)
 *
 * Wind/Hail deductible can be either a decimal percentage (< 1, e.g., 0.01 = 1%) or a dollar amount (>= 1)
 * and must be converted to a dollar amount using the appropriate coverage before comparison.
 *
 * Related issue: #28860
 */
export const validateFO6WindHailDeductible = function (message) {
  return this.test('fo6-wind-hail-deductible-validation', message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');

    // Only validate for FO6 risks
    if (![FarmRiskType.Fo6Structures, FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(riskType)) {
      return true;
    }

    const allPerilDeductible = getIn(formRiskData, 'coverages.deductibleAllOther');

    // If there's no All-Peril deductible set, skip validation
    if (!allPerilDeductible) {
      return true;
    }

    // Skip validation if Wind/Hail deductible is not set (field is nullable)
    // Check before converting to Number to properly handle null/undefined
    if (value === null || value === undefined) {
      return true;
    }

    const windHailValue = Number(value);
    const allPerilValue = Number(allPerilDeductible);

    // Determine which coverage to use based on FO6 risk type
    let coverage = 0;
    let coverageLabel = '';

    if (riskType === FarmRiskType.Fo6Structures) {
      coverage = getIn(formRiskData, 'coverages.coverageE') || 0;
      coverageLabel = 'E';
    } else if (riskType === FarmRiskType.Fo6Scheduled) {
      // coverageF is an array of items with 'limit' property - sum all limits
      const coverageFArray = getIn(formRiskData, 'coverages.coverageF');
      coverage = Array.isArray(coverageFArray)
        ? coverageFArray.reduce((sum, item) => sum + (item?.limit || 0), 0)
        : coverageFArray || 0;
      coverageLabel = 'F';
    } else if (riskType === FarmRiskType.Fo6Unscheduled) {
      coverage = getIn(formRiskData, 'coverages.coverageG') || 0;
      coverageLabel = 'G';
    }

    // Calculate actual dollar amounts
    // Wind/Hail can be either percentage (< 1) or dollar amount (>= 1)
    const windHailDollarAmount = windHailValue < 1 ? (coverage ? windHailValue * coverage : null) : windHailValue;

    // All-Peril can be either a percentage (< 1) or dollar amount (>= 1)
    const allPerilDollarAmount =
      allPerilValue < 1 ? (coverage ? Math.round(coverage * allPerilValue) : 0) : allPerilValue;

    // If wind/hail is a percentage but coverage is missing, reject
    if (windHailValue < 1 && windHailDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based Wind/Hail deductible`
      });
    }

    // If all-peril is a percentage but coverage is missing, reject
    if (allPerilValue < 1 && allPerilDollarAmount === null) {
      return this.createError({
        message: `Coverage ${coverageLabel} is required to calculate percentage-based All-Peril deductible`
      });
    }

    // Round both sides to handle floating point precision issues
    const windHailRounded = windHailDollarAmount !== null ? Math.round(windHailDollarAmount) : null;
    const allPerilRounded = Math.round(allPerilDollarAmount);

    if (windHailRounded !== null && windHailRounded < allPerilRounded) {
      // Format display values for error message
      const windHailDisplay =
        windHailValue < 1
          ? `${+(windHailValue * 100).toFixed(2)}% ($${windHailRounded.toLocaleString()})`
          : `$${windHailDollarAmount.toLocaleString()}`;

      const allPerilDisplay =
        allPerilValue < 1
          ? `${+(allPerilValue * 100).toFixed(2)}% ($${allPerilRounded.toLocaleString()})`
          : `$${allPerilValue.toLocaleString()}`;

      return this.createError({
        message: `Wind/Hail deductible (${windHailDisplay}) cannot be less than All-Peril deductible (${allPerilDisplay}) for Coverage ${coverageLabel}`
      });
    }

    return true;
  });
};

// Register the method
Yup.addMethod(Yup.number, 'validateFO6WindHailDeductible', validateFO6WindHailDeductible);

/**
 * FO6 Structure Category Loss Settlement Validation
 *
 * This validation method enforces the business rules for structure category loss settlement
 * options at the schema level. It works in conjunction with the UI business logic in
 * FO6.utils.js to ensure data integrity.
 *
 * Validation Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Must be ACV only (roof: ACV or NONE with exclusion)
 * - Confinement, Poultry: Structure → ACV or RC; Roof → ACV, RC_ACV_WIND_HAIL, or NONE with exclusion
 * - Silos/Grain Bins: ACV or RC allowed (roof also allows NONE with exclusion; RC_ACV_WIND_HAIL is NOT valid)
 * - Barns/Outbuildings: Handled by existing building class validation
 *
 * @param {string} fieldName - Name of the field being validated (for error messages)
 * @param {string} message - Base validation error message
 * @returns {Yup.TestFunction} Yup test function
 */
export const validateStructureCategoryLossSettlement = function (fieldName, message) {
  return this.test(`${fieldName}-structure-category-validation`, message, function (value) {
    const formRiskData = this.from?.[1]?.value;
    if (!formRiskData) {
      return true; // Skip validation if form context unavailable
    }

    // Skip coverage validation for new risks (risks without an id)
    if (isNewRisk(formRiskData)) {
      return true;
    }

    const riskType = getIn(formRiskData, 'type');
    const buildingType = getIn(formRiskData, 'building.buildingType');

    // Only validate for FO6 Structures
    if (riskType !== FarmRiskType.Fo6Structures) {
      return true;
    }

    // Structure category-specific validation
    switch (buildingType) {
      case StructureCategory.DwellingContents:
      case StructureCategory.HoopBuildings:
      case StructureCategory.Fencing: {
        // Must be ACV only (NONE allowed for roof loss settlement when roof exclusion is enabled)
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'NONE'] : ['ACV'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" for this structure type`
          })
        );
      }

      case StructureCategory.ConfinementBuildings:
      case StructureCategory.PoultryBuildings: {
        // For structureLossSettlement: ACV or RC allowed (role restrictions handled in UI)
        // For roofLossSettlement: ACV or RC_ACV_WIND_HAIL only (plain RC is never valid for these types)
        // NONE allowed when roof exclusion is enabled
        // GitHub issue #29079
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC_ACV_WIND_HAIL', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: isRoofField
              ? `${fieldName} must be a valid option for this structure type`
              : `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.SilosGrainBins: {
        // ACV or RC allowed for both structure and roof loss settlement (RC_ACV_WIND_HAIL is NOT valid for silos)
        // NONE allowed for roof loss settlement when roof exclusion is enabled
        // GitHub issue #29079
        const isRoofField = fieldName === 'Roof Loss Settlement';
        const validValues = isRoofField ? ['ACV', 'RC', 'NONE'] : ['ACV', 'RC'];
        return (
          validValues.includes(value) ||
          this.createError({
            message: `${fieldName} must be "Actual Cash Value" or "Replacement Cost"`
          })
        );
      }

      case StructureCategory.BarnsOutbuildings:
        // Building class logic already handles this
        return true;

      default:
        return true;
    }
  });
};

// Register the method
Yup.addMethod(Yup.string, 'validateStructureCategoryLossSettlement', validateStructureCategoryLossSettlement);

const baseFarmValidationSchema = Yup.object().shape({
  farmCoverage: Yup.object().shape({
    policyLevelAdjustmentFactor: Yup.number()
      .transform((value, originalValue) => {
        // Convert empty strings to default value
        if (String(originalValue).trim() === '') {
          return 0;
        }
        // Convert NaN to default value
        if (Number.isNaN(value)) {
          return 0;
        }
        return value ?? 0;
      })
      .required()
      .min(-99, 'Adjustment factor must be between -99% and 99%')
      .max(99, 'Adjustment factor must be between -99% and 99%'),
    adjustmentRationale: Yup.string().nullable().optional().max(1000, 'Rationale must be 1,000 characters or fewer'),
    extraExpenseLimit: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // Convert empty strings to null
        if (String(originalValue).trim() === '') {
          return null;
        }
        return value;
      })
      .test('min-when-provided', 'Farm Extra Expense Coverage minimum limit is $5,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value >= 5000;
      })
      .test('max-when-provided', 'Farm Extra Expense Coverage maximum limit is $25,000', (value) => {
        // Skip validation if no coverage (null, undefined, or 0)
        if (!value) {
          return true;
        }
        return value <= 25000;
      })
  }),
  entity: Yup.object()
    .shape({
      id: Yup.string().nullable(),
      type: Yup.string()
        .nullable()
        .required('Add details about the entity requesting this offer')
        .test('joint-requires-coapplicant', 'A co-applicant is required when entity type is Joint', function (value) {
          // Only validate on Modify Offer (when offer.id exists)
          const offerId = this.options?.context?.offerId;

          // Skip validation for New Offer (no offer ID yet)
          if (!offerId) {
            return true;
          }

          // Only validate if entity type is "Joint"
          if (value === 'Joint') {
            // Get drivers array from root form values
            const formValues = this.from?.[this.from.length - 1]?.value;
            const drivers = formValues?.drivers;

            // Require at least 2 drivers AND one with isCoApplicant === true
            if (!drivers || drivers.length < 2) {
              return false;
            }

            const hasCoApplicant = drivers.some((driver) => driver.isCoApplicant === true);
            return hasCoApplicant;
          }

          return true;
        }),
      name: Yup.string()
        .nullable()
        .test('required-for-non-individual-entity', 'Entity name is required', function (value) {
          const entityType = this.parent.type;
          if (entityType && entityType !== 'Individual' && entityType !== 'Joint') {
            return !!value;
          }
          return true;
        }),
      address: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().nullable(),
          zip: Yup.string().nullable()
        })
        .nullable()
    })
    .test('has-entity-type', 'Add details about the entity requesting this offer', (value) => {
      // Ensure entity with a type is provided
      return value?.type;
    }),
  farmLocations: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      description: Yup.string().required('Description is required'),
      legalDescription: Yup.object().shape({
        format: Yup.string().required('Format is required'),
        section: Yup.string().nullable(),
        township: Yup.string().nullable(),
        townshipCoordinates: Yup.string().nullable(),
        range: Yup.string().nullable(),
        rangeCoordinates: Yup.string().nullable(),
        lot: Yup.string().nullable(),
        block: Yup.string().nullable()
      }),
      additionalLegalDescription: Yup.string().nullable(),
      locationDirections: Yup.string().nullable(),
      numberOfAcres: Yup.number().nullable(),
      address: Yup.object().shape({
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        country: Yup.string().nullable(),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zip: Yup.string().required('Zip code is required')
      }),
      fpc: Yup.string().nullable(),
      fpcFromLocation: Yup.string().nullable(),
      fireHydrantWithin1000ft: Yup.boolean().nullable(),
      latitude: Yup.number().nullifyIfNaN().nullable(),
      longitude: Yup.number().nullifyIfNaN().nullable(),
      risks: Yup.array().of(
        Yup.object().shape({
          type: Yup.string().required('Risk type is required'),
          description: Yup.string().required('Description is required'),
          adjustmentFactor: Yup.number()
            .transform((value, originalValue) => {
              // Convert empty strings to default value
              if (String(originalValue).trim() === '') {
                return 0;
              }
              // Convert NaN to default value
              if (Number.isNaN(value)) {
                return 0;
              }
              return value ?? 0;
            })
            .required()
            .min(-99, 'Adjustment factor must be between -99% and 99%')
            .max(99, 'Adjustment factor must be between -99% and 99%'),
          adjustmentFactorTotal: Yup.number()
            .nullable()
            .min(-99, 'Adjustment factor total must be between -99% and 99%')
            .max(99, 'Adjustment factor total must be between -99% and 99%'),
          building: Yup.object().shape({
            basementSqFt: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Basement sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            basementType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Basement/Foundation is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            constructionType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Construction type is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBuildings
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.DwellingContents
                    ]
                  },
                  {
                    risk: FarmRiskType.Fo3,
                    requireIfRatedOnly: true,
                    excludedPropertyTypes: ({ state }) =>
                      getConstructionTypeExemptPropertyTypes(state, FarmRiskType.Fo3)
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Construction type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            mobileHomeQualityGrade: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              }),
            mobileHomeConfig: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Mobile home config is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }]
              })
              .invalidMobileHomeConfigForFo3(
                'Single-wide and double-wide mobile homes are not eligible for FO-3. Please select FO-1 or FO-2.'
              ),
            exteriorWallType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Exterior wall finish is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageCarCapacity: Yup.number()
              .nullable()
              .requiredForRisks({
                message: "Garage's car space is required",
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            garageType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Garage type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            dwellingLocation: Yup.object().shape({
              address: Yup.string().nullable(),
              address2: Yup.string().nullable(),
              country: Yup.string().nullable(),
              city: Yup.string().nullable(),
              state: Yup.string().nullable(),
              zip: Yup.string().nullable()
            }),
            homeQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFamiliesInDwelling: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Families in dwelling is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFireplaces: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Fireplaces is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numFullBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Full bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numHalfBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Half bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numStories: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Stories is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            numWoodBurningStoves: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Wood stoves is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            ownershipStatus: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Ownership status is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            purchaseDate: Yup.string().nullable(),
            purchaseAmount: Yup.number().nullable(),
            roofOriginal: Yup.boolean().nullable(),
            roofShape: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof shape is required!',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Roof shape is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofDeck: Yup.string().nullable(),
            roofCover: Yup.string().nullable(),
            roofDeckAttachment: Yup.string().nullable(),
            roofToWallConnection: Yup.string().nullable(),
            openingProtection: Yup.string().nullable(),
            roofSWR: Yup.string().nullable(),
            roofType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof type is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            roofYear: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Roof year is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Roof year is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .requiredIfFO6DwellingContents('Roof year is required')
              .test('roof-year-max-4-digits', 'Roof year must be at most 4 digits', (value) => {
                if (value == null) {
                  return true;
                }
                return String(value).length <= 4;
              })
              .test(
                'roof-year-after-year-built',
                'Roof year must be same as or later than year built',
                function (value) {
                  const yearBuilt = getIn(this.parent, 'yearBuilt');

                  // Skip validation if either value is missing
                  if (!value || !yearBuilt) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(yearBuilt, 10);
                  const roofYearNum = parseInt(value, 10);

                  if (roofYearNum < yearBuiltNum) {
                    return this.createError({
                      message: `Roof year (${roofYearNum}) cannot be earlier than building year (${yearBuiltNum})`
                    });
                  }

                  return true;
                }
              ),
            roofYearChangedManually: Yup.boolean().nullable(),
            sqFt: Yup.number()
              .nullable()
              .requiredForNonTowerSilos('Square footage is required')
              .requiredForRisks({
                message: 'Square footage is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true }
                ]
              })
              .test('required-for-fo3-barndominium', 'Square footage is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null;
                }
                return true;
              })
              .min(150, 'Square footage must be greater than 150 square feet')
              .mobileHomeSqFtValidation(),
            originalSqFt: Yup.number().nullable(),
            swimmingPool: Yup.object()
              .nullable()
              .default(undefined)
              .shape({
                typeOfPool: Yup.string()
                  .nullable()
                  .test('required-swimming-pool-type', 'Type of pool is required', function (value) {
                    // Swimming pool type is intentionally required for FO1, FO2, FO3, FO4 risk types.
                    // This ensures users must explicitly select a pool type (or "None") before finalizing.
                    // Check if this risk type requires swimming pool type
                    if (requiresSwimmingPoolType(this.options)) {
                      return !!value;
                    }
                    return true;
                  }),
                hasRemovableStepsOrLockingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-above-ground',
                    'This field is required for above-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'A') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                has4FtHighFenceWithSelfLatchingGate: Yup.boolean()
                  .nullable()
                  .test(
                    'required-for-dwelling-in-ground',
                    'This field is required for in-ground pools',
                    function (value) {
                      const typeOfPool = getIn(this.parent, 'typeOfPool');
                      if (isDwellingOrRentersRisk(this.options) && typeOfPool === 'I') {
                        return value !== null && value !== undefined;
                      }
                      return true;
                    }
                  ),
                hasDivingBoardOrPoolSlide: Yup.boolean()
                  .nullable()
                  .test('required-for-dwelling-with-pool', 'This field is required', function (value) {
                    const typeOfPool = getIn(this.parent, 'typeOfPool');
                    if (isDwellingOrRentersRisk(this.options) && (typeOfPool === 'A' || typeOfPool === 'I')) {
                      return value !== null && value !== undefined;
                    }
                    return true;
                  }),
                limit: Yup.number().nullable()
              }),
            typeOfHome: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home type is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            occupancy: Yup.string()
              .nullable()
              .requiredIfFO6BarnsOutbuildings('Occupancy is required')
              .requiredIfFO6SilosGrainBins('Occupancy is required')
              .requiredIfFO6ConfinementBuildings('Occupancy is required'),
            residenceType: Yup.string().nullable().requiredIfFO6DwellingContents('Residence type is required'),
            yearBuilt: Yup.string()
              .nullable()
              .validYearBuilt()
              .test('year-built-max-4-digits', 'Year built must be at most 4 digits', (value) => {
                if (value == null) {
                  return true;
                }
                return String(value).length <= 4;
              })
              .requiredForRisks({
                message: 'Year built is required',
                risks: [{ risk: FarmRiskType.Fo3, requireIfRatedOnly: true }]
              })
              .test('required-for-fo3-barndominium', 'Year built is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return value != null && String(value).trim() !== '';
                }
                return true;
              })
              .requiredForFO6Structures('Year built is required')
              .requiredIfFO6DwellingContents('Year built is required')
              .test(
                'year-built-building-class-1-compatibility',
                'Building age incompatible with Building Class 1 requirements',
                function (value) {
                  // Access sibling fields within the building object
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Skip risk type check for manual validation - rely on field conditions only
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  if (
                    buildingClass !== 1 ||
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings
                  ) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const yearBuilt = parseInt(value, 10);
                  const buildingAge = yearBuilt ? currentYear - yearBuilt : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Building Class 1 requires age ≤ maxAge years AND no open sides
                  const shouldFail = hasOpenSides || (buildingAge !== null && buildingAge > maxAge);

                  if (shouldFail) {
                    return false;
                  }

                  return true;
                }
              )
              .test(
                'year-built-before-roof-year',
                'Building year must be same as or earlier than roof year',
                function (value) {
                  const roofYear = getIn(this.parent, 'roofYear');

                  // Skip validation if either value is missing
                  if (!value || !roofYear) {
                    return true;
                  }

                  const yearBuiltNum = parseInt(value, 10);
                  const roofYearNum = parseInt(roofYear, 10);

                  if (yearBuiltNum > roofYearNum) {
                    return this.createError({
                      message: `Building year (${yearBuiltNum}) cannot be later than roof year (${roofYearNum})`
                    });
                  }

                  return true;
                }
              ),
            latitude: Yup.number().nullifyIfNaN().nullable(),
            longitude: Yup.number().nullifyIfNaN().nullable(),
            stormShutters: Yup.boolean().nullable(),
            porchSqFt: Yup.number().nullable(),
            porchCode: Yup.string().nullable(),
            constructionStyle: Yup.string().nullable(),
            areasUnderConstruction: Yup.boolean()
              .nullable()
              .test('renovation-fields-required', 'Renovation details are incomplete', function (value) {
                if (!value) {
                  return true;
                }
                const renovation = this.parent?.renovation;
                const renovationFields = [
                  'electricalType',
                  'electricalService',
                  'heatingType',
                  'heatingYear',
                  'coolingYear',
                  'plumbingYear',
                  'electricalYear'
                ];
                const hasMissing = renovationFields.some((field) => !renovation?.[field]);
                if (!hasMissing) {
                  return true;
                }
                const riskDescription = this.from?.[1]?.value?.description || 'this risk';
                return this.createError({
                  message: `You've indicated ${riskDescription} has renovations, please provide those details or uncheck the box to continue`
                });
              }),
            hasTrampoline: Yup.boolean().nullable(),
            // Mobile home specific fields
            tieDownType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Tie down type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] },
                  { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                ]
              }),
            enclosedMasonryFoundation: Yup.boolean().nullable(),
            fullSkirtingAroundBase: Yup.boolean().nullable(),
            // Fields for both Barndominium and MobileHome
            renovation: Yup.object()
              .shape({
                electricalType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalService: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical service is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingType: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating type is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                heatingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Heating year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                coolingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'AC year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                plumbingYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Plumbing year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  }),
                electricalYear: Yup.string()
                  .nullable()
                  .test('required-if-renovations', 'Electrical year is required', function (value) {
                    const building = this.from?.[1]?.value;
                    if (!building?.areasUnderConstruction) {
                      return true;
                    }
                    return !!value;
                  })
              })
              .nullable(),
            attachedStructureSqFt: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Attached garage sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
              }),
            // Well pump - all fields required if any is filled
            numWellPumps: Yup.number()
              .nullable()
              .test('well-pump-required', 'Number of pumps is required', function (value) {
                const { wellPumpEstimatedValue, wellPumpDescription } = this.parent;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasEstValue || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              })
              .test('well-pump-min', 'Number of pumps must be at least 1', (value) => {
                if (value != null) {
                  return value >= 1;
                }
                return true;
              }),
            wellPumpEstimatedValue: Yup.number()
              .nullable()
              .test('well-pump-value-required', 'Estimated value is required', function (value) {
                const { numWellPumps, wellPumpDescription } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasDescription = wellPumpDescription != null && wellPumpDescription.trim() !== '';

                if (hasNumPumps || hasDescription) {
                  return value != null && value > 0;
                }
                return true;
              }),
            wellPumpDescription: Yup.string()
              .nullable()
              .test('well-pump-description-required', 'Description of pumps or motors is required', function (value) {
                const { numWellPumps, wellPumpEstimatedValue } = this.parent;
                const hasNumPumps = numWellPumps != null && numWellPumps > 0;
                const hasEstValue = wellPumpEstimatedValue != null && wellPumpEstimatedValue > 0;

                if (hasNumPumps || hasEstValue) {
                  return value != null && value.trim() !== '';
                }
                return true;
              }),
            numSolidFuelBurningAppliances: Yup.number()
              .nullable()
              .min(0, 'Number of appliances cannot be negative')
              .requiredForRisks({
                message: 'Solid fuel burning appliances count is required',
                risks: [
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] },
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [StructureCategory.BarnsOutbuildings, StructureCategory.PoultryBuildings]
                  },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              }),
            primaryHeatingSource: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Primary heating source is required',
                risks: [
                  { risk: FarmRiskType.Fo1, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo2, requireIfRatedOnly: true },
                  { risk: FarmRiskType.Fo3, requireIfRatedOnly: true },
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              })
              .test('required-for-fo3-barndominium', 'Primary heating source is required', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }
                const riskType = getIn(formRiskData, 'type');
                const propertyType = getIn(this.parent, 'propertyType');
                if (riskType === FarmRiskType.Fo3 && propertyType === 'B') {
                  return !!value;
                }
                return true;
              }),
            secondaryHeatingSource: Yup.string().nullable(),
            buildingQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Quality is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBuildings,
                      StructureCategory.ConfinementBuildings,
                      StructureCategory.HoopBuildings,
                      StructureCategory.DwellingContents
                    ]
                  }
                ]
              }),
            buildingClass: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Building class is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [StructureCategory.BarnsOutbuildings]
                  }
                ]
              })
              .test(
                'building-class-1-age-eligibility',
                'Building Class 1 requires eligible building age and no open sides',
                function (value) {
                  // Only validate for FO6 Barns/Outbuildings with Building Class 1
                  const buildingType = getIn(this.parent, 'buildingType');
                  const riskType = FarmRiskType.Fo6Structures; // Assume FO6 since this is in FO6 validation

                  if (
                    riskType !== FarmRiskType.Fo6Structures ||
                    buildingType !== StructureCategory.BarnsOutbuildings ||
                    value !== 1
                  ) {
                    return true;
                  }

                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const hasOpenSides = getIn(this.parent, 'structureOpenSides');

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const formRiskData = this.from?.[1]?.value;
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // Class 1 requires age ≤ maxAge years AND no open sides
                  if (buildingAge !== null && buildingAge > maxAge) {
                    return this.createError({
                      message: `Building Class 1 requires building age ≤ ${maxAge} years. Current building age: ${buildingAge} years.`
                    });
                  }

                  if (hasOpenSides) {
                    return this.createError({
                      message: 'Building Class 1 cannot have open sides.'
                    });
                  }
                  return true;
                }
              ),
            structureOpenSides: Yup.boolean()
              .nullable()
              .test('no-open-sides-for-class-1', 'Class 1 structures cannot have open sides', function (value) {
                const buildingClass = getIn(this.parent, 'buildingClass');
                return !(buildingClass === 1 && value === true);
              })
              .test(
                'no-open-sides-for-barn-type-1',
                'Buildings of this age cannot have open sides (violates Type 1 requirements)',
                function (value) {
                  // Access sibling fields within the building object
                  const yearBuilt = getIn(this.parent, 'yearBuilt');
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type from parent context (navigate up to risk level)
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }

                  const riskType = getIn(formRiskData, 'type');

                  // Only validate for FO6 Barns/Outbuildings with Class 1
                  // Class 2 and 3 are allowed to have open sides regardless of age
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  if (buildingClass !== 1) {
                    return true;
                  }

                  const currentYear = new Date().getFullYear();
                  const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                  // Try to extract state from form context, fall back to IL (most restrictive)
                  const state = (formRiskData && getIn(formRiskData, 'address.state')) || 'IL';
                  const maxAge = getBuildingClass1MaxAge(state);

                  // If building age <= maxAge, it would be Type 1, so cannot have open sides
                  if (buildingAge !== null && buildingAge <= maxAge && value === true) {
                    return this.createError({
                      message: `Buildings ${maxAge} years old or newer cannot have open sides (violates Type 1 requirements)`
                    });
                  }

                  return true;
                }
              )
              .test(
                'open-sides-type-1-eligibility',
                'Type 1 barns cannot have open sides. Remove Type 1 coverages or fix building characteristics.',
                function (value) {
                  // Access sibling fields within the building object
                  const buildingType = getIn(this.parent, 'buildingType');
                  const buildingClass = getIn(this.parent, 'buildingClass');

                  // Get risk type and coverage data
                  const formRiskData = this.from?.[1]?.value;
                  if (!formRiskData) {
                    return true; // Skip validation if form context unavailable
                  }
                  const riskType = getIn(formRiskData, 'type');
                  const coverages = getIn(formRiskData, 'coverages');

                  // Only validate for FO6 Barns/Outbuildings
                  if (riskType !== FarmRiskType.Fo6Structures || buildingType !== StructureCategory.BarnsOutbuildings) {
                    return true;
                  }

                  // Class 2 and 3 are allowed to have open sides even with the same coverages
                  if (buildingClass !== 1) {
                    return true;
                  }

                  // Check if this appears to be configured as Type 1 (has Type 1 coverages)
                  const hasType1Coverages =
                    coverages?.structureLossSettlement === 'RC' &&
                    coverages?.specialFormCoverage === true &&
                    coverages?.hasSnowWeightCoverage === true;

                  // If configured with Type 1 coverages but has open sides, block
                  if (hasType1Coverages && value === true) {
                    return this.createError({
                      message: 'Building configured as Type 1 cannot have open sides.'
                    });
                  }

                  return true;
                }
              ),
            numHeatingSources: Yup.number().nullable(),
            numSecondaryHeatingSources: Yup.number().nullable(),
            diameter: Yup.string().nullable().requiredForTowers('Diameter is required'),
            height: Yup.string()
              .nullable()
              .test('required-sg-height', 'Height is required', function (value) {
                const buildingType = getIn(this.parent, 'buildingType');
                const occupancy = getIn(this.parent, 'occupancy');
                // Height is hidden for Bulk Bin/Hopper (GHH) and length/width types (CC, GS, GH)
                const hiddenHeightOccupancies = ['GHH', 'CC', 'GS', 'GH'];
                if (buildingType === 'SG' && occupancy && !hiddenHeightOccupancies.includes(occupancy)) {
                  return !!value;
                }
                return true;
              }),
            length: Yup.string().nullable(),
            width: Yup.string().nullable(),
            bushelCapacity: Yup.string().nullable().requiredForGrainStorage('Bushel capacity is required'),
            containsInGroundPool: Yup.boolean().nullable(),
            residenceUsage: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Residence usage is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }],
                requireIfRatedOnly: true
              }),
            propertyType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Structure category is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }]
              })
          }),
          coverages: Yup.object().shape({
            coverageA: Yup.number()
              .coverageInRangeRequiredWhenEditable([FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo3] }),
            coverageB: Yup.number().nullable(),
            coverageC: Yup.number().nullable().validateDwellingCoverageC('Invalid Coverage C value'),
            coverageD: Yup.number().nullable().validateFO1CoverageD('Invalid Coverage D value'),
            coverageE: Yup.number()
              .coverageInRangeRequiredWhenEditable([
                FarmRiskType.Fo6Unscheduled,
                FarmRiskType.Fo6Scheduled,
                FarmRiskType.Fo6Structures
              ])
              .nullable()
              .rc80PercentRequirement({ validRiskTypes: [FarmRiskType.Fo6Structures] })
              .requiredIfFO6DwellingContents('Coverage E - Dwelling Limit is required')
              .requiredIfFO6Fencing('Coverage E - Dwelling Limit is required'),
            coverageF: Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string().nullable(),
                  category: Yup.string().nullable(),
                  limit: Yup.number().nullable(),
                  description: Yup.string().nullable(),
                  deductible: Yup.number().nullable(),
                  deductibleWindHail: Yup.number().nullable(),
                  additionalPerils: Yup.number().nullable(),
                  multipleLossCoverage: Yup.boolean().nullable(),
                  livestockCategory: Yup.string().nullable(),
                  hasOpenPerils: Yup.boolean().nullable(),
                  hasReplacementCostCoverage: Yup.boolean().nullable(),
                  serialNumber: Yup.string().nullable(),
                  make: Yup.string().nullable(),
                  increaseStackLimit: Yup.number().nullable(),
                  quantity: Yup.number().nullable().integer().min(0),
                  hasWinterPerils: Yup.boolean().nullable(),
                  hasSpecialCattleEndorsement: Yup.boolean().nullable(),
                  hasSuffocationOfLivestock: Yup.boolean().nullable()
                })
              )
              .nullable(),
            coverageG: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Blanket limit is required',
                risks: [{ risk: FarmRiskType.Fo6Unscheduled }]
              })
              .test('coverageG-greater-than-zero', 'Blanket limit must be greater than $0', function (value) {
                const risk = this.from?.[1]?.value;
                if (!risk || risk.type !== FarmRiskType.Fo6Unscheduled) {
                  return true;
                }
                if (isNewRisk(risk)) {
                  return true;
                }
                if (value === null || value === undefined) {
                  return true; // handled by requiredForRisks
                }
                return value > 0;
              }),
            coverageMS: Yup.boolean().nullable(),
            hasActiveCoverageMS: Yup.boolean().nullable(),
            deductible: Yup.number().nullable(),
            deductibleAllOther: Yup.number().nullable(),
            deductibleWindHail: Yup.number()
              .nullable()
              .validateWindHailDeductible('Invalid Wind/Hail deductible')
              .validateFO6WindHailDeductible('Invalid Wind/Hail deductible for FO6'),
            deductibleEarthquake: Yup.number().nullable(),
            deductibleEarthquake2: Yup.number().nullable(),
            roofExclusion: Yup.string().nullable(),
            roofExclusionDetails: Yup.string().nullable(),
            exteriorWallSurfacingExclusion: Yup.boolean().nullable(),
            exteriorWallSurfacingExclusionDetails: Yup.string()
              .nullable()
              .when('exteriorWallSurfacingExclusion', {
                is: (value) => !!value,
                then: (schema) =>
                  schema.required('Description is required when exterior wall surfacing exclusion is enabled'),
                otherwise: (schema) => schema.nullable()
              }),
            otherStructuresExclusion: Yup.boolean().nullable(),
            otherStructuresExclusionDetails: Yup.string().nullable(),
            hasSnowWeightCoverage: Yup.boolean().nullable().barnType1Validation(),
            structureLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Structure Loss Settlement')
              .barnType1Validation(),
            roofLossSettlement: Yup.string()
              .nullable()
              .validateStructureCategoryLossSettlement('Roof Loss Settlement')
              .test('roof-acv-constraint', 'Roof Loss Settlement cannot be RC when Structure is ACV', function (value) {
                const structureLoss = getIn(this.parent, 'structureLossSettlement');
                if (structureLoss === 'ACV' && value === 'RC') {
                  return this.createError({
                    message:
                      'Roof Loss Settlement cannot be "Replacement Cost" when Structure Loss Settlement is "Actual Cash Value"'
                  });
                }
                return true;
              }),
            specialFormCoverage: Yup.boolean().nullable().barnType1Validation(),
            windHailExclusion: Yup.boolean().nullable(),
            windHailExclusionDetails: Yup.string().nullable(),
            otherStructuresRoofExclusion: Yup.boolean().nullable(),
            sidingExclusion: Yup.boolean().nullable(),
            sidingExclusionDetails: Yup.string().nullable(),
            stormDamageExclusion: Yup.boolean().nullable(),
            stormDamageExclusionDetails: Yup.string().nullable(),
            scheduleCoverages: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    coverageType: Yup.string().nullable(),
                    items: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.string().nullable(),
                          category: Yup.string()
                            .nullable()
                            .test('category-required-by-coverage-type', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && !value) {
                                return this.createError({ message: 'Kind of animal is required' });
                              }
                              if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                return this.createError({ message: 'Type of item is required' });
                              }
                              if (ct === FarmScheduleCoverageType.INCOME && !value) {
                                return this.createError({ message: 'Type of item is required' });
                              }
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Item type is required' });
                              }
                              return true;
                            }),
                          name: Yup.string()
                            .nullable()
                            .test('name-required-spp', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Item name is required' });
                              }
                              return true;
                            }),
                          limit: Yup.number()
                            .nullable()
                            .test('limit-required-by-coverage-type', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (
                                ct === FarmScheduleCoverageType.ANIMALS_4H_FFA ||
                                ct === FarmScheduleCoverageType.PEAK ||
                                ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES ||
                                ct === FarmScheduleCoverageType.SPP
                              ) {
                                if (value == null) {
                                  return this.createError({ message: 'Coverage limit is required' });
                                }
                              }
                              if (ct === FarmScheduleCoverageType.INCOME) {
                                if (value == null) {
                                  return this.createError({ message: 'Limit is required' });
                                }
                                if (value < 5000) {
                                  return this.createError({ message: 'Limit minimum is $5,000' });
                                }
                                if (value > 25000) {
                                  return this.createError({ message: 'Limit maximum is $25,000' });
                                }
                              }
                              return true;
                            }),
                          description: Yup.string()
                            .nullable()
                            .test('description-required-spp', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct === FarmScheduleCoverageType.SPP && !value) {
                                return this.createError({ message: 'Description is required' });
                              }
                              return true;
                            }),
                          deductible: Yup.number()
                            .nullable()
                            .test('deductible-required-spp', function (value) {
                              const ct = this.from?.[1]?.value?.coverageType;
                              if (ct !== FarmScheduleCoverageType.SPP) {
                                return true;
                              }
                              const category = this.parent?.category;
                              const alwaysRequired = ['scheduledGlass', 'computerHardware', 'computerSoftware'];
                              if (alwaysRequired.includes(category)) {
                                if (value == null) {
                                  return this.createError({ message: 'Deductible is required' });
                                }
                                return true;
                              }
                              const state =
                                this.options?.context?.farmLocations?.[0]?.address?.state ||
                                this.options?.context?.correctedAddress?.state;
                              if (state !== 'IL' && value == null) {
                                return this.createError({ message: 'Deductible is required' });
                              }
                              return true;
                            }),
                          extendedDetails: Yup.object()
                            .nullable()
                            .default(undefined)
                            .shape({
                              name: Yup.string()
                                .nullable()
                                .test('extName-required-by-coverage-type', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                    return this.createError({ message: 'Name is required' });
                                  }
                                  if (ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES && !value) {
                                    return this.createError({ message: 'Structure name is required' });
                                  }
                                  return true;
                                }),
                              year: Yup.number()
                                .nullable()
                                .test('year-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiringYear = ['boat', 'atv', 'golfCart', 'unmannedAircraft'];
                                  if (!typesRequiringYear.includes(category)) {
                                    return true;
                                  }
                                  if (value == null) {
                                    return this.createError({ message: 'Year is required' });
                                  }
                                  if (value < 1900) {
                                    return this.createError({ message: 'Year must be 1900 or later' });
                                  }
                                  const currentYear = new Date().getFullYear();
                                  if (value > currentYear) {
                                    return this.createError({
                                      message: `Year must be ${currentYear} or earlier`
                                    });
                                  }
                                  return true;
                                }),
                              horsepower: Yup.number()
                                .nullable()
                                .test('horsepower-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boat' && value == null) {
                                    return this.createError({ message: 'Horsepower is required' });
                                  }
                                  return true;
                                }),
                              serialNumber: Yup.string()
                                .nullable()
                                .test('serialNumber-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['boat', 'unmannedAircraft', 'atv', 'golfCart'];
                                  if (typesRequiring.includes(category) && !value) {
                                    return this.createError({ message: 'Serial Number is required' });
                                  }
                                  return true;
                                }),
                              classification: Yup.string()
                                .nullable()
                                .test('classification-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boat' && !value) {
                                    return this.createError({ message: 'Classification is required' });
                                  }
                                  return true;
                                }),
                              make: Yup.string()
                                .nullable()
                                .test('make-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['golfCart', 'atv', 'unmannedAircraft'];
                                  if (typesRequiring.includes(category) && !value) {
                                    return this.createError({ message: 'Make is required' });
                                  }
                                  return true;
                                }),
                              model: Yup.string()
                                .nullable()
                                .test('model-required-spp', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  const typesRequiring = ['golfCart', 'atv', 'unmannedAircraft'];
                                  if (typesRequiring.includes(category) && !value) {
                                    return this.createError({ message: 'Model is required' });
                                  }
                                  return true;
                                }),
                              cc: Yup.mixed()
                                .nullable()
                                .test('cc-required-atv', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'atv' && (value == null || value === '')) {
                                    return this.createError({ message: 'CC is required' });
                                  }
                                  return true;
                                }),
                              weight: Yup.number()
                                .nullable()
                                .test('weight-required-drone', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'unmannedAircraft' && value == null) {
                                    return this.createError({ message: 'Weight is required' });
                                  }
                                  return true;
                                }),
                              lengthFt: Yup.number()
                                .nullable()
                                .test('lengthFt-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boat' && value == null) {
                                    return this.createError({ message: 'Length is required' });
                                  }
                                  return true;
                                }),
                              milesPerHour: Yup.number()
                                .nullable()
                                .test('milesPerHour-required-boat', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boat' && value == null) {
                                    return this.createError({ message: 'Miles per hour is required' });
                                  }
                                  return true;
                                }),
                              dimensions: Yup.string()
                                .nullable()
                                .test('dimensions-required-boatDock', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.SPP) {
                                    return true;
                                  }
                                  const category = this.from?.[1]?.value?.category;
                                  if (category === 'boatDock' && !value) {
                                    return this.createError({ message: 'Dimensions are required' });
                                  }
                                  return true;
                                }),
                              kindOfAnimal: Yup.string().nullable(),
                              sex: Yup.string()
                                .nullable()
                                .test('sex-required-animals', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && !value) {
                                    return this.createError({ message: 'Sex is required' });
                                  }
                                  return true;
                                }),
                              breed: Yup.string().nullable(),
                              age: Yup.number().nullable(),
                              originalCost: Yup.number()
                                .nullable()
                                .test('originalCost-required-animals', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.ANIMALS_4H_FFA && value == null) {
                                    return this.createError({ message: 'Original cost is required' });
                                  }
                                  return true;
                                }),
                              coinsurance: Yup.mixed()
                                .nullable()
                                .test('coinsurance-required-income', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.INCOME && (value == null || value === '')) {
                                    return this.createError({ message: 'Coinsurance is required' });
                                  }
                                  return true;
                                }),
                              days: Yup.mixed().nullable(),
                              startDate: Yup.string()
                                .nullable()
                                .test('startDate-required-peak', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.PEAK && !value) {
                                    return this.createError({ message: 'Start date is required' });
                                  }
                                  return true;
                                }),
                              endDate: Yup.string()
                                .nullable()
                                .test('endDate-required-and-after-start-peak', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct !== FarmScheduleCoverageType.PEAK) {
                                    return true;
                                  }
                                  if (!value) {
                                    return this.createError({ message: 'End date is required' });
                                  }
                                  const startDate = this.parent?.startDate;
                                  if (startDate && new Date(value) < new Date(startDate)) {
                                    return this.createError({
                                      message: 'End date must be after start date'
                                    });
                                  }
                                  return true;
                                }),
                              description: Yup.string()
                                .nullable()
                                .test('extDescription-required-rps', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES && !value) {
                                    return this.createError({ message: 'Description is required' });
                                  }
                                  return true;
                                }),
                              deductibleAllOther: Yup.mixed()
                                .nullable()
                                .test('deductibleAllOther-required-rps', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (
                                    ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES &&
                                    (value == null || value === '')
                                  ) {
                                    return this.createError({ message: 'All peril deductible is required' });
                                  }
                                  return true;
                                }),
                              deductibleWindHail: Yup.mixed()
                                .nullable()
                                .test('deductibleWindHail-required-rps', function (value) {
                                  const ct = this.from?.[2]?.value?.coverageType;
                                  if (
                                    ct === FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES &&
                                    (value == null || value === '')
                                  ) {
                                    return this.createError({ message: 'Wind & Hail deductible is required' });
                                  }
                                  return true;
                                }),
                              coverageMS: Yup.boolean().nullable(),
                              hasActiveCoverageMS: Yup.boolean().nullable()
                            })
                        })
                      )
                      .nullable()
                  })
                  .nullable()
              )
              .nullable()
              .test(
                'required-schedule-coverages-for-fo6-scheduled',
                'At least one schedule coverage is required',
                (value, { options }) => {
                  const [locationPath, riskPath] = options.path.split('.');
                  const risk = getIn(options.context, `${locationPath}.${riskPath}`);
                  if (!risk || risk.type !== FarmRiskType.Fo6Scheduled) {
                    return true;
                  }
                  // Only validate rated risks (those with a backend-generated id)
                  if (!risk.id) {
                    return true;
                  }
                  // coverageF items satisfy the requirement (they ARE scheduled farm property items) (#29771)
                  const coverageF = getIn(options.context, `${locationPath}.${riskPath}.coverages.coverageF`);
                  if (Array.isArray(coverageF) && coverageF.length > 0) {
                    return true;
                  }
                  // It's OK to have no scheduled items when the location has other rated risks
                  // besides FO6 Scheduled/Unscheduled (e.g., FO1-FO4 or FO6 Structures) (#29818)
                  const location = getIn(options.context, locationPath);
                  const hasOtherRatedRisks =
                    Array.isArray(location?.risks) &&
                    location.risks.some(
                      (r) => r.id && r.type !== FarmRiskType.Fo6Scheduled && r.type !== FarmRiskType.Fo6Unscheduled
                    );
                  if (hasOtherRatedRisks) {
                    return true;
                  }
                  // Otherwise check scheduleCoverages
                  if (!Array.isArray(value) || value.length === 0) {
                    return false;
                  }
                  return value.some((coverage) => Array.isArray(coverage?.items) && coverage.items.length > 0);
                }
              ),
            coverageWaterBackup: Yup.number().nullable(),
            coverageISBC: Yup.number().nullable(),
            coverageRSExtended: Yup.boolean().nullable(),
            poolLimit: Yup.number()
              .nullable()
              .test('required-pool-limit', 'Coverage limit is required', function (value) {
                // Pool limit is required only for above-ground pools on FO1-FO4 risks (#29233).
                // In-ground pools no longer expose a dwelling-level coverage limit — agents
                // schedule the pool under Related Private Structures instead (#30984).
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true;
                }

                const riskType = formRiskData.type;

                if (![FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo4].includes(riskType)) {
                  return true;
                }

                const typeOfPool = formRiskData.building?.swimmingPool?.typeOfPool;

                if (typeOfPool === 'A' && (value === null || value === undefined)) {
                  return this.createError({ message: 'Coverage limit is required' });
                }

                if (typeOfPool === 'A' && value < 2500) {
                  return this.createError({
                    message: 'Above-ground pool total coverage cannot be less than the included $2,500 base'
                  });
                }

                return true;
              }),
            resUnderConstructionLimit: Yup.number().nullable(),
            resUnderConstructionEffectiveDate: Yup.string().nullable(),
            inlandFloodInceptionDate: Yup.string().nullable(),
            inlandFloodOccurrenceLimit: Yup.number().nullable(),
            inlandFloodAggregateLimit: Yup.number().nullable(),
            inlandFloodDeductible: Yup.number().nullable(),
            inlandFloodDeductibleOverride: Yup.number().nullable(),
            inlandFloodRiskScore: Yup.number().nullable(),
            expandedDebrisRemoval: Yup.boolean().nullable(),
            serviceLine: Yup.boolean().nullable(),
            personalPropertyReplCost: Yup.boolean().nullable(),
            businessPropertyLimit: Yup.number().nullable(),
            businessPropertyLimitDetails: Yup.string().nullable(),
            moneyLimit: Yup.number().nullable(),
            jewelryLimit: Yup.number().nullable(),
            securitiesLimit: Yup.number().nullable(),
            motorizedVehiclesLimit: Yup.number().nullable(),
            silverwareLimit: Yup.number().nullable(),
            gunsLimit: Yup.number().nullable(),
            tenantImprovementLimit: Yup.number().nullable(),
            sprinklerLeakageBuildingLimit: Yup.number().nullable(),
            sprinklerLeakageLimitDetails: Yup.string()
              .nullable()
              .when('sprinklerLeakageBuildingLimit', {
                is: (value) => value != null && value > 0,
                then: Yup.string().required(
                  'Description of premise is required when sprinkler leakage limit is provided'
                ),
                otherwise: Yup.string().nullable()
              }),
            canEditCoverageAmount: Yup.boolean().nullable(),
            adjustDwellingLimitDescription: Yup.string()
              .requiredWhenCanEditCoverageAmount('Description of reason for adjusting dwelling limit is required')
              .nullable(),
            cosmeticDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Cosmetic damage exclusion is required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              })
              .test(
                'at-least-one-exclusion-type',
                'You must have at least one cosmetic damage surfacing type selected in order to exclude it.',
                function (value) {
                  if (!value) {
                    return true;
                  }

                  const { exteriorDoorsWindowsDamageExclusion, exteriorWallsDamageExclusion, roofsDamageExclusion } =
                    this.parent;

                  return exteriorDoorsWindowsDamageExclusion || exteriorWallsDamageExclusion || roofsDamageExclusion;
                }
              ),
            exteriorDoorsWindowsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              }),
            exteriorWallsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              }),
            roofsDamageExclusion: Yup.boolean()
              .nullable()
              .test('required-for-fo6', 'Required for FO-6 structures', function (value) {
                const formRiskData = this.from?.[1]?.value;
                if (!formRiskData) {
                  return true; // Skip validation if form context unavailable
                }

                // Skip coverage validation for new risks (risks without an id)
                if (isNewRisk(formRiskData)) {
                  return true;
                }

                const riskType = getIn(formRiskData, 'type');
                if (riskType === FarmRiskType.Fo6Structures) {
                  return value === true;
                }
                return true;
              })
          }),
          connectedHome: Yup.object().shape({
            monitored: Yup.string().nullable(),
            moistureDevices: Yup.number().nullable(),
            theftPreventionDevices: Yup.number().nullable(),
            theftPreventionDeviceType: Yup.mixed().nullable(),
            motionDetectingDevices: Yup.number().nullable(),
            smokeDetectingDevices: Yup.number().nullable(),
            fireProtectiveDeviceType: Yup.mixed().nullable(),
            internetMonitoring: Yup.boolean().nullable(),
            autoWaterShutoffDevices: Yup.boolean().nullable(),
            providerName: Yup.string().nullable(),
            agreeToTerms: Yup.boolean().nullable()
          })
        })
      )
    })
  ),
  farmInterestedParties: Yup.array()
    .when('global.farmPaymentMethod', {
      is: (farmPaymentMethod) => farmPaymentMethod === paymentMethod.BillToOther,
      then: Yup.array()
        .test(
          'billToOther-exists',
          'A Bill To Other party is required when Bill To Other payment method is selected',
          (farmInterestedParties) => {
            if (!farmInterestedParties || farmInterestedParties.length === 0) {
              return false;
            }
            return farmInterestedParties.some((party) => party.relationship === 'billToOther');
          }
        )
        .of(
          Yup.object().when('relationship', {
            is: 'billToOther',
            then: Yup.object().shape({
              name: Yup.string().required('Bill To Other name is required').nullable(),
              address: Yup.object()
                .shape({
                  address: Yup.string().required('Bill To Other address is required').nullable(),
                  address2: Yup.string().nullable(),
                  city: Yup.string().required('Bill To Other city is required').nullable(),
                  state: Yup.string()
                    .validUSState('address.state')
                    .required('Bill To Other state is required')
                    .nullable(),
                  zip: Yup.string().required('Bill To Other zip is required').nullable()
                })
                .required('Bill To Other address is required')
                .nullable()
            }),
            otherwise: getFarmInterestedPartyItemSchema(false)
          })
        )
        .nullable(),
      otherwise: Yup.array().of(getFarmInterestedPartyItemSchema(false)).nullable()
    })
    .nullable(),
  drivers: Yup.array()
    .of(
      Yup.object().shape({
        dateOfBirth: Yup.string()
          .nullable()
          .required('Date of birth is required')
          .test('minimum-driver-age', '', function (value) {
            if (value) {
              const driver = this.options?.context?.drivers?.[this.options?.index];
              const firstName = driver?.firstName ?? '';
              const lastName = driver?.lastName ?? '';
              const today = new Date();
              const birthDate = new Date(value);
              const age = differenceInYears(today, birthDate);

              if (age < 14) {
                return this.createError({
                  message: `${firstName} ${lastName} is too young to be on the policy`,
                  path: this.path
                });
              }
            }
            return true;
          }),
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required')
      })
    )
    .nullable(),
  farmLiabilityCoverage: Yup.object()
    .nullable()
    .when('includeFarmLiability', {
      is: true,
      then: (schema) =>
        schema.shape({
          // Issue #31838: when liability is toggled on the agent must first pick a
          // coverage type. Without this rule the only liability error was the
          // farmOperations one below, so an agent who hadn't picked a type saw
          // "Primary farm operations is required" (a field that's even hidden for
          // Home/Rental) instead of the real problem.
          type: Yup.string().nullable().required('Please select type of liability coverage'),
          // Issue #31351: Clarion Door rejects the rate request when
          // `primary_operations` is empty, so the field must be picked before
          // submit whenever liability is included. Gated on `type` so that when no
          // type is selected yet the agent sees the type error first (#31838) — and
          // because farmOperations is only meaningful/visible once a type is chosen.
          farmOperations: Yup.string()
            .nullable()
            .when('type', {
              is: (type) => Boolean(type),
              then: (schema) => schema.required('Primary farm operations is required'),
              otherwise: (schema) => schema.nullable()
            }),
          domesticEmployees: Yup.array()
            .of(
              Yup.object().shape({
                firstName: Yup.string().required('First name is required'),
                lastName: Yup.string().required('Last name is required')
              })
            )
            .nullable(),
          occupancies: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Occupancy type is required'),
                description: Yup.string().required('Occupancy description is required'),
                location: Yup.string().required('Location description is required')
              })
            )
            .nullable(),
          customFarmWorkItems: Yup.array()
            .of(
              Yup.object().shape({
                description: Yup.string().required('Description is required'),
                amount: Yup.number().required('Estimated receipts is required').positive('Must be a positive number')
              })
            )
            .nullable(),
          businessActivities: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Business activity type is required'),
                description: Yup.string().required('Description is required'),
                includesSpecificActivities: Yup.boolean().nullable(),
                namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
              })
            )
            .nullable(),
          businessPursuits: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.number().required('Type is required'),
                amount: Yup.number().required('Estimated receipts is required'),
                namedInsureds: Yup.array().of(Yup.string().required('Named insured is required'))
              })
            )
            .nullable(),
          customFeeding: Yup.array()
            .of(
              Yup.object().shape({
                type: Yup.string().required('Operations is required'),
                description: Yup.string().required('Description is required'),
                associatedLocations: Yup.array().of(Yup.string().required('Associated location is required'))
              })
            )
            .nullable(),
          additionalAssetsExtensions: Yup.array()
            .of(
              Yup.object().shape({
                coverage: Yup.string().nullable().required('Coverage is required'),
                name: Yup.string()
                  .nullable()
                  .test('asset-name-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type) && !value) {
                      return this.createError({ message: 'Name is required' });
                    }
                    return true;
                  }),
                year: Yup.number()
                  .nullable()
                  .test('asset-year-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (!['atv', 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type)) {
                      return true;
                    }
                    if (value == null) {
                      return this.createError({ message: 'Year is required' });
                    }
                    if (value < 1900) {
                      return this.createError({ message: 'Year must be 1900 or later' });
                    }
                    const currentYear = new Date().getFullYear();
                    if (value > currentYear) {
                      return this.createError({ message: `Year must be ${currentYear} or earlier` });
                    }
                    return true;
                  }),
                make: Yup.string()
                  .nullable()
                  .test('asset-make-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', 'golfCart', 'unmannedAircraft'].includes(item.type) && !value) {
                      return this.createError({ message: 'Make is required' });
                    }
                    return true;
                  }),
                model: Yup.string()
                  .nullable()
                  .test('asset-model-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', 'golfCart', 'unmannedAircraft'].includes(item.type) && !value) {
                      return this.createError({ message: 'Model is required' });
                    }
                    return true;
                  }),
                serialNumber: Yup.string()
                  .nullable()
                  .test('asset-serialNumber-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (['atv', 'golfCart', 'unmannedAircraft', 'boat'].includes(item.type) && !value) {
                      return this.createError({ message: 'Serial number is required' });
                    }
                    return true;
                  }),
                cc: Yup.mixed()
                  .nullable()
                  .test('asset-cc-required-atv', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'atv' && (value == null || value === '')) {
                      return this.createError({ message: 'CC is required' });
                    }
                    return true;
                  }),
                weight: Yup.number()
                  .nullable()
                  .test('asset-weight-required-drone', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'unmannedAircraft' && value == null) {
                      return this.createError({ message: 'Weight is required' });
                    }
                    return true;
                  }),
                description: Yup.string()
                  .nullable()
                  .test('asset-description-required-drone', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'unmannedAircraft' && !value) {
                      return this.createError({ message: 'Description is required' });
                    }
                    return true;
                  }),
                classification: Yup.string()
                  .nullable()
                  .test('asset-classification-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && !value) {
                      return this.createError({ message: 'Classification is required' });
                    }
                    return true;
                  }),
                horsepower: Yup.number()
                  .nullable()
                  .test('asset-horsepower-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Horsepower is required' });
                    }
                    return true;
                  }),
                length: Yup.number()
                  .nullable()
                  .test('asset-length-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Length is required' });
                    }
                    return true;
                  }),
                mph: Yup.number()
                  .nullable()
                  .test('asset-mph-required-boat', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId || item.existingAssetId) {
                      return true;
                    }
                    if (item.type === 'boat' && value == null) {
                      return this.createError({ message: 'Miles per hour is required' });
                    }
                    return true;
                  })
              })
            )
            .nullable(),
          additionalPropertiesExtensions: Yup.array()
            .of(
              Yup.object().shape({
                coverage: Yup.string().nullable().required('Coverage is required'),
                additionalLegalDescription: Yup.string().nullable(),
                locationDirections: Yup.string().nullable(),
                structureDetail: Yup.string().nullable(),
                numFamilies: Yup.number()
                  .nullable()
                  .test('numFamilies-required-rented-non-structure', function (value) {
                    const item = this.parent;
                    const RENTED_COVERAGES_SET = new Set([
                      'locationRentedToOthers',
                      'structureRentedToOthers',
                      'residenceRentedToOthers'
                    ]);
                    if (!RENTED_COVERAGES_SET.has(item.coverage)) {
                      return true;
                    }
                    if (item.type === 'structure') {
                      return true;
                    }
                    if (value == null) {
                      return this.createError({ message: 'Number of families is required' });
                    }
                    return true;
                  }),
                description: Yup.string()
                  .nullable()
                  .test('property-description-required-not-on-policy', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId) {
                      return true;
                    }
                    if (item.type === 'location' && !value) {
                      return this.createError({ message: 'Location name is required' });
                    }
                    if ((item.type === 'residence' || item.type === 'structure') && !value) {
                      return this.createError({ message: 'Item name is required' });
                    }
                    return true;
                  }),
                numberOfAcres: Yup.number()
                  .nullable()
                  .integer('Must be a whole number')
                  .test('property-numberOfAcres-required-location', function (value) {
                    const item = this.parent;
                    if (item.locationId || item.riskId) {
                      return true;
                    }
                    if (item.type === 'location' && value == null) {
                      return this.createError({ message: 'Acreage is required' });
                    }
                    return true;
                  }),
                legalDescription: Yup.object()
                  .nullable()
                  .default(undefined)
                  .shape({
                    format: Yup.string()
                      .nullable()
                      .test('legalDescription-format-required-location', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && !value) {
                          return this.createError({ message: 'Legal Description Format is required' });
                        }
                        return true;
                      }),
                    section: Yup.string()
                      .nullable()
                      .test('legalDescription-section-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Section is required' });
                        }
                        return true;
                      }),
                    township: Yup.string()
                      .nullable()
                      .test('legalDescription-township-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Township is required' });
                        }
                        return true;
                      }),
                    range: Yup.string()
                      .nullable()
                      .test('legalDescription-range-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (
                          item.type === 'location' &&
                          item.legalDescription?.format === 'section-township-range' &&
                          !value
                        ) {
                          return this.createError({ message: 'Range is required' });
                        }
                        return true;
                      }),
                    lot: Yup.string()
                      .nullable()
                      .test('legalDescription-lot-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'lot-block' && !value) {
                          return this.createError({ message: 'Lot is required' });
                        }
                        return true;
                      }),
                    block: Yup.string()
                      .nullable()
                      .test('legalDescription-block-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'lot-block' && !value) {
                          return this.createError({ message: 'Block is required' });
                        }
                        return true;
                      }),
                    townshipCoordinates: Yup.string().nullable(),
                    rangeCoordinates: Yup.string().nullable()
                  }),
                addressDetails: Yup.object()
                  .nullable()
                  .default(undefined)
                  .shape({
                    address: Yup.string()
                      .nullable()
                      .test('addressDetails-address-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type === 'residence' && !value) {
                          return this.createError({ message: 'Address is required' });
                        }
                        if (item.type === 'location' && item.legalDescription?.format === 'street' && !value) {
                          return this.createError({ message: 'Address is required' });
                        }
                        return true;
                      }),
                    address2: Yup.string().nullable(),
                    city: Yup.string()
                      .nullable()
                      .test('addressDetails-city-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'City is required' });
                        }
                        return true;
                      }),
                    state: Yup.string()
                      .nullable()
                      .test('addressDetails-state-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'State is required' });
                        }
                        return true;
                      }),
                    zip: Yup.string()
                      .nullable()
                      .test('addressDetails-zip-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if ((item.type === 'location' || item.type === 'residence') && !value) {
                          return this.createError({ message: 'ZIP is required' });
                        }
                        return true;
                      }),
                    county: Yup.string()
                      .nullable()
                      .test('addressDetails-county-required', function (value) {
                        const item = this.from?.[1]?.value;
                        if (!item || item.locationId || item.riskId) {
                          return true;
                        }
                        if (item.type !== 'location') {
                          return true;
                        }
                        const format = item.legalDescription?.format;
                        if (format && format !== 'street' && !value) {
                          return this.createError({ message: 'County is required' });
                        }
                        return true;
                      })
                  })
              })
            )
            .nullable(),
          careProvidedToOthers: Yup.number().nullable(),
          careProvidedToOthersDescription: Yup.string()
            .nullable()
            .when('careProvidedToOthers', {
              is: (value) => value > 0,
              then: (schema) => schema.required('Description is required'),
              otherwise: (schema) => schema.nullable()
            }),
          employees: Yup.object()
            .nullable()
            .shape({
              fullTime: Yup.number()
                .required()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number'),
              partTime: Yup.number()
                .required()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number'),
              partTime40DaysOrLess: Yup.number()
                .required()
                .min(0, 'Must be 0 or greater')
                .max(99, 'Must be 99 or less')
                .integer('Must be a whole number')
            }),
          exclusions: Yup.object()
            .nullable()
            .shape({
              excludeActivities: Yup.boolean().nullable(),
              excludedActivitiesDescription: Yup.string()
                .nullable()
                .when('excludeActivities', {
                  is: true,
                  then: (schema) => schema.required('Description of excluded activities is required'),
                  otherwise: (schema) => schema.nullable()
                }),
              excludeLocations: Yup.boolean().nullable(),
              excludedLocationsDescription: Yup.string()
                .nullable()
                .when('excludeLocations', {
                  is: true,
                  then: (schema) => schema.required('Description of excluded locations is required'),
                  otherwise: (schema) => schema.nullable()
                })
            })
        })
    })
});

export const getSharedFarmSchema = async () => {
  let schema = baseFarmValidationSchema;

  try {
    const brandValidationModule = await import(`./brand-schemas/${brandConstants.SHORT_NAME?.toLowerCase()}.js`);

    if (brandValidationModule.default) {
      schema = schema.concat(brandValidationModule.default);
    }
  } catch {
    // Brand validation schema not found - silently continue with base schema
  }

  return schema;
};
